import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'
import { COLORS } from '~theme/colors'

export type OnChangeHandlerT = (
  e: any,
  newValues: any[],
  reason: AutocompleteChangeReason,
  details?: AutocompleteChangeDetails<any> | undefined,
  helperInput?: string,
) => void

export function MultiItemAutocomplete({
  input = [],
  setInputValue,
  label,
  onChangeHandler,
  options,
  hasError,
  disabled,
  persistOptionsScroll,
  helperInput,
  onInputChangeHandler,
  ...rest
}: {
  helperInput?: string[]
  input?: string[]
  setInputValue?: (value: string) => void
  label: ReactNode
  onChangeHandler: OnChangeHandlerT
  options: any[]
  hasError?: boolean
  disabled?: boolean
  persistOptionsScroll?: boolean
  [key: string]: any
}) {
  const [open, setOpen] = useState<boolean>(Boolean(helperInput?.length))
  const [localInputValue, setLocalInputValue] = useState<string>(
    helperInput?.[0] ?? '',
  )

  useEffect(() => {
    setOpen(Boolean(helperInput?.length))
    setLocalInputValue(helperInput?.[0] ?? '')
  }, [helperInput])

  if (!options || !Array.isArray(options)) {
    return null
  }

  const handleChange = (
    e: any,
    newValues: string[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any> | undefined,
  ) => {
    onChangeHandler?.(e, newValues, reason, details, localInputValue)
    if (reason === 'selectOption') {
      setOpen(false)
    }
  }

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={input}
      inputValue={localInputValue}
      slotProps={
        !persistOptionsScroll || helperInput?.length
          ? {}
          : {
              listbox: {
                role: 'list-box',
                sx: {
                  '& .MuiAutocomplete-option': {
                    '&:hover': {
                      backgroundColor: COLORS.greys.ultraLight,
                    },
                  },
                },
              },
            }
      }
      freeSolo
      multiple
      disablePortal
      disableClearable
      forcePopupIcon={false}
      options={options}
      filterSelectedOptions
      onChange={handleChange}
      renderInput={(params) => (
        <TextField
          {...params}
          slotProps={{
            htmlInput: {
              ...(params.inputProps ?? {}),
              onKeyDown: (event: any) => {
                if (event.key === 'Tab') {
                  setOpen(false)
                }
                params.inputProps?.onKeyDown?.(event)
              },
            },
          }}
          label={label}
          placeholder={disabled ? '' : 'Search...'}
          error={hasError}
        />
      )}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'input') {
          setOpen(true)
        }
        setLocalInputValue(newInputValue ?? '')
        setInputValue?.(newInputValue ?? '')
        onInputChangeHandler?.(
          event,
          newInputValue ?? '',
          reason,
          helperInput?.[0] ?? '',
        )
      }}
      disabled={disabled}
      disableCloseOnSelect
      limitTags={1}
      size="small"
      slots={{
        popper: CustomPopper,
        paper: (props) => (
          <Paper
            {...props}
            sx={
              open && Boolean(helperInput?.length)
                ? {
                    border: `2px solid ${COLORS.secondary.actionBlue}`,
                    borderTop: 'none',
                  }
                : undefined
            }
          >
            {props.children}
            {options.length === 0 && !!helperInput?.length && (
              <Typography sx={{ p: 2, color: 'text.secondary' }}>
                No options
              </Typography>
            )}
          </Paper>
        ),
      }}
      {...rest}
    />
  )
}

export function CustomPopper(props: any) {
  return (
    <Popper
      {...props}
      sx={{
        '& .MuiAutocomplete-listbox': {
          maxHeight: '250px',
          overflowY: 'auto',
        },
      }}
    />
  )
}
